import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router';

import { EmptyState } from '../components/EmptyState';
import { ErrorMessage } from '../components/ErrorMessage';
import { PageLoader } from '../components/Loader';
import { useProject } from '../projects/context';
import { projectKeys, useProjectApi } from '../projects/useProjectApi';
import { fmtDateTime } from './format';
import { confirmationOpen, phoneConfirmationView } from './phoneConfirmation';
import { PhoneConfirmationActions } from './PhoneConfirmationActions';

// Rubrik „Terminbestätigungen“ im Support (OFFENE_FRAGEN B-29): alle anstehenden Maklertermine, die telefonisch zu bestätigen
// sind – nach Fälligkeit (Werktag davor 16 Uhr, kurzfristig 2 Stunden vorher), Überfällige zuerst. „Nicht erreicht“ bleibt in
// der Liste (erneut versuchen), der Termin bleibt bestehen.
export function PhoneConfirmations() {
  const { project } = useProject();
  const papi = useProjectApi();
  const [state, setState] = useState<'open' | 'done'>('open');
  const [now] = useState(() => new Date());
  const list = useQuery({
    queryKey: projectKeys.phoneConfirmations(project.id, state),
    queryFn: ({ signal }) => papi.phoneConfirmations(state, signal),
    refetchInterval: 60_000,
  });
  return (
    <div className="embedded-page phone-confirmations">
      <p className="muted">
        Vor jedem Maklertermin ruft die Terminplanung den Kunden an. „Nicht erreicht“ ist keine Absage – der Termin bleibt, der Makler fährt. Bei einer
        Absage erfährt es der Makler über App, E-Mail und Kalender, und es entsteht die Aufgabe, ihn persönlich anzurufen.
      </p>
      <div className="chips" role="group" aria-label="Auswahl">
        <button type="button" className={state === 'open' ? 'chip active' : 'chip'} aria-pressed={state === 'open'} onClick={() => setState('open')}>
          Anzurufen
        </button>
        <button type="button" className={state === 'done' ? 'chip active' : 'chip'} aria-pressed={state === 'done'} onClick={() => setState('done')}>
          Ergebnisse (3 Tage)
        </button>
      </div>
      {list.isPending ? (
        <PageLoader label="Terminbestätigungen werden geladen …" />
      ) : list.isError ? (
        <ErrorMessage error={list.error} onRetry={() => void list.refetch()} />
      ) : list.data.items.length === 0 ? (
        <EmptyState title={state === 'open' ? 'Nichts anzurufen' : 'Keine Ergebnisse'} icon="calendar">
          {state === 'open' ? 'Alle anstehenden Termine sind bestätigt.' : 'In den letzten drei Tagen wurde kein Bestätigungsanruf eingetragen.'}
        </EmptyState>
      ) : (
        <div className="table-wrap">
          <table className="table contacts-table">
            <thead>
              <tr>
                <th scope="col">Termin</th>
                <th scope="col">Kunde</th>
                <th scope="col">Stand</th>
                <th scope="col">Ergebnis eintragen</th>
              </tr>
            </thead>
            <tbody>
              {list.data.items.map((it) => {
                const view = it.confirmation ? phoneConfirmationView(it.confirmation, now) : null;
                return (
                  <tr key={it.appointment_id} className={it.overdue ? 'row-attention' : undefined}>
                      <td>
                        <strong>{fmtDateTime(it.start_at)}</strong>
                        <small className="muted cell-sub">
                          {it.field_sales_name ?? 'ohne Makler'}
                          {it.type_name ? ` · ${it.type_name}` : ''}
                        </small>
                      </td>
                      <td>
                        <Link to={`/p/${project.key}/termine/${it.appointment_id}`}>{it.lead_name ?? it.contact_name ?? 'Termin'}</Link>
                        <small className="muted cell-sub">
                          {[it.city, it.phone].filter(Boolean).join(' · ')}
                          {it.booked_by_name ? ` · gebucht von ${it.booked_by_name}` : ''}
                        </small>
                      </td>
                      <td>
                        <div className="followup-state">
                          {view && <span className={`badge badge-${view.tone}`}>{view.label}</span>}
                          {view && <small className="cell-sub">{view.text}</small>}
                        </div>
                      </td>
                      <td>
                        {confirmationOpen(it.confirmation) ? (
                          <PhoneConfirmationActions appointmentId={it.appointment_id} leadId={it.lead_id} leadName={it.lead_name ?? 'Termin'} />
                        ) : (
                          <span className="muted">{it.confirmation?.note ?? '–'}</span>
                        )}
                      </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
